import { useCallback, useEffect, useRef, useState } from "react";
import {
  api,
  ApiError,
  type Answer,
  type Result,
  type RunSession,
} from "../shared/api";
import { Challenge } from "./components/Challenge";
import { PageShell } from "./components/PageShell";
const token = new URLSearchParams(location.search).get("launch") ?? "";
function LiveLogin({ onSuccess }: { onSuccess: () => void }) {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [until, setUntil] = useState(() =>
    Number(sessionStorage.getItem("sms-until") ?? 0),
  );
  const [clock, setClock] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const remaining = Math.max(0, Math.ceil((until - clock) / 1000));
  async function act(login: boolean) {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      if (!/^1[3-9]\d{9}$/.test(phone)) throw Error("请输入有效手机号");
      if (login) {
        await api("/auth/login", { phone, code, launch: token });
        onSuccess();
      } else {
        const result = await api<{ resend_interval: number }>("/auth/sms", {
          phone,
          launch: token,
        });
        const next = Date.now() + result.resend_interval * 1000;
        setUntil(next);
        sessionStorage.setItem("sms-until", String(next));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <h1>登录后继续原题</h1>
      <p>使用手机号验证码登录，课件播放位置已保留。</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void act(true);
        }}
      >
        <label className="field">
          <span>手机号</span>
          <input
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            maxLength={11}
            required
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="option"
          disabled={busy || remaining > 0}
          onClick={() => void act(false)}
        >
          {remaining ? `${remaining} 秒后重发` : "获取短信验证码"}
        </button>
        <label className="field">
          <span>验证码</span>
          <input
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </label>
        {error && (
          <p role="alert" className="error">
            {error}
          </p>
        )}
        <button className="primary-button" disabled={busy || code.length !== 6}>
          登录并继续
        </button>
      </form>
    </>
  );
}
export function LiveApp() {
  const drafts = useRef<Record<string, Answer>>({});
  const initialLoad = useRef(true);
  const swipe = useRef<{ x: number; y: number } | null>(null);
  const [session, setSession] = useState<RunSession>();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(0);
  const [redoKey, setRedoKey] = useState<string>();
  const load = useCallback(async () => {
    setError("");
    try {
      const loaded = await api<RunSession>(
        "/sessions/" + encodeURIComponent(token),
      );
      setSession(loaded);
      if ((loaded.authenticated || loaded.guest) && initialLoad.current) {
        const next =
          loaded.questions?.findIndex((item) => !loaded.answers?.[item.id]) ??
          0;
        setCurrent(Math.max(0, next));
        initialLoad.current = false;
      }
      if (!loaded.authenticated && !loaded.guest)
        history.replaceState(
          null,
          "",
          "/course-quiz/login?launch=" + encodeURIComponent(token),
        );
    } catch (e) {
      if (e instanceof ApiError && e.status === 401)
        setSession({ authenticated: false, title: "课堂练习" });
      else setError(e instanceof Error ? e.message : String(e));
    }
  }, []);
  useEffect(() => {
    if (token) void load();
  }, [load]);
  const q = session?.questions?.[current];
  const result = q ? session?.answers?.[q.id] : undefined;
  async function redo() {
    if (!q || busy) return;
    setBusy(true);
    setError("");
    const requestId = redoKey ?? crypto.randomUUID();
    setRedoKey(requestId);
    try {
      await api("/sessions/" + token + "/redo", {
        questionId: q.id,
        requestId,
      });
      drafts.current[q.id] = {};
      setSession((previous) => {
        if (!previous) return previous;
        const answers = { ...previous.answers };
        delete answers[q.id];
        return { ...previous, answers };
      });
      setRedoKey(undefined);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  async function submit(response: Answer) {
    if (!q || busy) return;
    setBusy(true);
    setError("");
    try {
      const saved = await api<Result>("/sessions/" + token + "/attempts", {
        questionId: q.id,
        response,
      });
      delete drafts.current[q.id];
      setSession((previous) =>
        previous
          ? { ...previous, answers: { ...previous.answers, [q.id]: saved } }
          : previous,
      );
    } catch (e) {
      if (e instanceof ApiError && e.status === 401)
        setSession({
          authenticated: false,
          title: session?.title ?? "课堂练习",
        });
      else setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  async function complete() {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api<{ returnUrl: string }>(
        "/sessions/" + token + "/complete",
        {},
      );
      const target = new URL(r.returnUrl, location.origin);
      if (
        target.origin !== location.origin ||
        !target.pathname.startsWith("/courses/")
      )
        throw Error("返回地址无效");
      location.replace(target.href);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }
  function navigate(index: number) {
    if (busy || index < 0 || index >= (session?.questions?.length ?? 0)) return;
    setCurrent(index);
    setRedoKey(undefined);
    setError("");
    window.scrollTo(0, 0);
  }
  if (token && (session?.authenticated || session?.guest) && q)
    return (
      <main
        className="course-challenge"
        onTouchStart={(e) => {
          const target = e.target as HTMLElement;
          if (
            target.closest("button,a,dialog,.comparison-scroll,.match-board") ||
            e.touches.length !== 1
          ) {
            swipe.current = null;
            return;
          }
          swipe.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        }}
        onTouchCancel={() => {
          swipe.current = null;
        }}
        onTouchEnd={(e) => {
          const start = swipe.current;
          swipe.current = null;
          if (!start || !e.changedTouches.length) return;
          const dx = e.changedTouches[0].clientX - start.x,
            dy = e.changedTouches[0].clientY - start.y;
          if (Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 2)
            navigate(current + (dx < 0 ? 1 : -1));
        }}
      >
        <header className="challenge-meta-row">
          <span className="lesson-title">
            {q.stem.description || session.title}
          </span>
          <span>
            {current + 1} / {session.questions?.length}
          </span>
        </header>
        {session.guest && <p className="lesson-title">免登录练习 · 不保存个人学习记录</p>}
        {error && (
          <p role="alert" className="error">
            {error}
          </p>
        )}
        <Challenge
          key={q.id}
          question={q}
          initialDraft={drafts.current[q.id]}
          onDraftChange={(value) => {
            drafts.current[q.id] = value;
          }}
          result={result}
          busy={busy}
          onSubmit={(response) => void submit(response)}
          onRedo={() => void redo()}
        />
        <nav className="course-navigation">
          <button
            disabled={busy || current === 0}
            onClick={() => navigate(current - 1)}
          >
            上一题
          </button>
          {current + 1 < (session.questions?.length ?? 0) ? (
            <button disabled={busy} onClick={() => navigate(current + 1)}>
              下一题
            </button>
          ) : (
            <button
              disabled={
                busy ||
                session.questions?.some((item) => !session.answers?.[item.id])
              }
              onClick={() => void complete()}
            >
              返回课件继续播放
            </button>
          )}
        </nav>
        <a
          className="course-return"
          onClick={event => {
            if (session.questions?.length && session.questions.every(item => session.answers?.[item.id])) {
              event.preventDefault();
              void complete();
            }
          }}
          aria-disabled={busy}
          href={
            session.returnUrl?.startsWith("/courses/")
              ? session.returnUrl
              : "/courses/blood/"
          }
        >
          {session.guest ? "返回课件（保留本次临时进度）" : "返回课件（保留已提交进度）"}
        </a>
      </main>
    );
  return (
    <PageShell
      title={session?.title ?? "课件互动练习"}
      action={<span className="counter">学成练习</span>}
    >
      {error && (
        <>
          <p className="error" role="alert">
            {error}
          </p>
          <button className="option" onClick={() => void load()}>
            重新加载
          </button>
        </>
      )}
      {!token ? (
        <>
          <h1>在真实课堂中练习</h1>
          <p>从动画课件进入老师配置的学成题，完成后回到原播放位置。</p>
          <a className="primary-button link" href="/courses/blood/">
            打开动画课件
          </a>
          <a
            className="back-link"
            href={
              import.meta.env.VITE_ADMIN_URL ||
              "http://localhost:4357/course-quiz-config"
            }
          >
            管理课件插题
          </a>
        </>
      ) : !session ? (
        <h1>正在加载练习…</h1>
      ) : !session.authenticated && !session.guest ? (
        <LiveLogin
          onSuccess={() => {
            history.replaceState(null, "", "/course-quiz/run?launch=" + token);
            void load();
          }}
        />
      ) : (
        <h1>当前检查点没有可用题目</h1>
      )}
      {token && (
        <a
          className="back-link"
          href={
            session?.returnUrl?.startsWith("/courses/")
              ? session.returnUrl
              : "/courses/blood/"
          }
        >
          返回课件（保留已提交进度）
        </a>
      )}
    </PageShell>
  );
}
